import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { templateService } from "../../services";
import { accountService } from "../../../master/services";
import { getCurrentUser, isRegularUser } from "../../../core/utils/auth";
import Modal from "../../../components/common/Modal";

// --- Icons matching the new design ---
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);
const FunnelIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
);
const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);
const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);
const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);
const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);
const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);
const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);
const DocumentDuplicateIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
  </svg>
);
const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);
const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);
const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);
const ArrowTopRightOnSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
  </svg>
);
const PhoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);
const ChatBubbleLeftEllipsisIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a.75.75 0 0 1-.974-.94 4.568 4.568 0 0 0 .584-2.12C3.486 16.55 3 14.39 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
  </svg>
);
const PhotoIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
  </svg>
);
const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>
);

// Helper to extract content from different template formats (Meta / Karix / Pinnacle)
const extractTemplateContent = (tpl) => {
  if (!tpl) {
    return {
      headerText: "",
      headerType: "NONE",
      bodyText: "",
      footerText: "",
      buttons: [],
      variables: [],
    };
  }

  const components = tpl.obj_message?.components || [];
  let headerText = tpl.str_header_text || tpl.obj_message?.header || "";
  let headerType = "NONE";
  let bodyText = tpl.str_body_text || tpl.obj_message?.body || "";
  let footerText = tpl.str_footer_text || tpl.obj_message?.footer || "";
  let buttons = Array.isArray(tpl.obj_message?.buttons) ? [...tpl.obj_message.buttons] : [];

  if (Array.isArray(components) && components.length > 0) {
    components.forEach((comp) => {
      const type = (comp.type || "").toUpperCase();
      if (type === "HEADER") {
        headerType = (comp.format || (comp.text ? "TEXT" : "MEDIA")).toUpperCase();
        if (comp.text) headerText = comp.text;
      } else if (type === "BODY") {
        if (comp.text) {
          bodyText = comp.text;
        } else if (comp.add_security_recommendation !== undefined || (tpl?.str_category || tpl?.category || "").toUpperCase() === "AUTHENTICATION") {
          bodyText = `{{1}} is your verification code.${comp.add_security_recommendation !== false ? " For your security, do not share this code." : ""}`;
        }
      } else if (type === "FOOTER") {
        if (comp.text) {
          footerText = comp.text;
        } else if (comp.code_expiration_minutes) {
          footerText = `This code expires in ${comp.code_expiration_minutes} minutes.`;
        }
      } else if (type === "BUTTONS") {
        if (Array.isArray(comp.buttons) && comp.buttons.length > 0) {
          buttons = comp.buttons;
        }
      }
    });
  }

  if (headerText && headerType === "NONE") {
    headerType = "TEXT";
  }

  // Extract variables like {{1}}, {{2}} from bodyText
  const variableMatches = bodyText ? bodyText.match(/\{\{\d+\}\}/g) || [] : [];
  const uniqueVariables = Array.from(new Set(variableMatches));

  return {
    headerText,
    headerType,
    bodyText,
    footerText,
    buttons,
    variables: uniqueVariables,
  };
};

const getLanguageLabel = (lang) => {
  const map = {
    en_US: "English (US)",
    en_GB: "English (UK)",
    en: "English",
    hi: "Hindi",
    es: "Spanish",
    ar: "Arabic",
    pt_BR: "Portuguese (BR)",
    id: "Indonesian",
  };
  return map[lang] || lang || "en_US";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  } catch {
    return "-";
  }
};

const formatCategory = (cat) => {
  if (!cat) return "-";
  const s = String(cat).trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const renderStatusBadge = (status) => {
  const s = (status || "draft").toLowerCase();
  if (s === "approved") {
    return (
      <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-emerald-100/90 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
        approved
      </span>
    );
  }
  if (s === "rejected") {
    return (
      <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-red-100/90 text-red-800 dark:bg-red-950/60 dark:text-red-300">
        rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-amber-100/90 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
      {s === "pending" ? "pending" : "draft"}
    </span>
  );
};

export default function TemplateManagement() {
  const navigate = useNavigate();
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isClient = useMemo(() => isRegularUser(currentUser), [currentUser]);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAccountId, setFilterAccountId] = useState("all");
  const [filterWabaId, setFilterWabaId] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [templatesPerPage, setTemplatesPerPage] = useState(10);

  // Accounts Metadata (for SuperAdmin/Admin view)
  const [accounts, setAccounts] = useState([]);

  // WABA Metadata & Sync States
  const [wabaList, setWabaList] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedSyncWaba, setSelectedSyncWaba] = useState("all");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const copyToClipboard = (text, key) => {
    if (!text) return;
    try {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error("Clipboard copy error:", err);
    }
  };

  const tplContent = useMemo(() => {
    return extractTemplateContent(selectedTemplate);
  }, [selectedTemplate]);

  const tplName = selectedTemplate?.str_template_name || selectedTemplate?.name || "";
  const tplCategory = (selectedTemplate?.str_category || selectedTemplate?.category || "MARKETING").toUpperCase();
  const tplLanguage = selectedTemplate?.str_language || selectedTemplate?.language || "en_US";
  const tplStatus = (selectedTemplate?.str_status || selectedTemplate?.status || "APPROVED").toUpperCase();
  const tplWaba = selectedTemplate?.str_waba_id || "";
  const tplProviderId = selectedTemplate?.str_provider_template_id || selectedTemplate?.id || "";
  const tplCompany = selectedTemplate?.str_company_name || "";

  // Load Accounts for Admin/SuperAdmin
  const loadAccounts = useCallback(async () => {
    if (isClient) return;
    try {
      const res = await accountService.get({ limit: 100 });
      if (res && res.success) {
        setAccounts(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load accounts:", err);
    }
  }, [isClient]);

  // Account options for dropdown
  const accountOptions = useMemo(() => {
    return accounts.map((acc) => {
      const accId = acc.int_account_id;
      const code = acc.str_account_code || `ACC${String(accId).padStart(6, "0")}`;
      const name = acc.str_company_name || "";
      return {
        value: accId,
        code,
        name,
        label: `${code} ${name ? `· ${name}` : ""}`.trim(),
      };
    });
  }, [accounts]);

  // Load Templates from Backend
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 1000 };
      if (filterAccountId && filterAccountId !== "all") {
        params.accountId = filterAccountId;
      }
      if (filterWabaId && filterWabaId !== "all") {
        params.waba_id = filterWabaId;
      }
      const res = await templateService.get(params);
      if (res && res.success) {
        setTemplates(res.data || []);
      } else if (Array.isArray(res)) {
        setTemplates(res);
      }
    } catch (err) {
      console.error("Failed to load templates:", err);
    } finally {
      setLoading(false);
    }
  }, [filterAccountId, filterWabaId]);

  // Load Available WABA IDs for the User/Account
  const loadWabaList = useCallback(async () => {
    try {
      const params = {};
      if (filterAccountId && filterAccountId !== "all") {
        params.accountId = filterAccountId;
      }
      const res = await templateService.getWabaIds(params);
      if (res && res.success && Array.isArray(res.data)) {
        setWabaList(res.data);
      }
    } catch (err) {
      console.error("Failed to load WABA IDs:", err);
    }
  }, [filterAccountId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadTemplates();
    loadWabaList();
  }, [loadTemplates, loadWabaList]);

  // Sync Action Handler
  const handleOpenSync = () => {
    if (wabaList.length === 0) {
      Swal.fire({
        title: "No WABA ID Found",
        text: "No WhatsApp Business Account (WABA ID) is provisioned. Please assign a WhatsApp phone number with a valid WABA ID in Phone Numbers first.",
        icon: "warning",
        confirmButtonColor: "#059669",
      });
      return;
    }

    // Always open modal to let user choose WABA or Sync All
    setSelectedSyncWaba(wabaList.length === 1 ? wabaList[0].waba_id : "all");
    setShowSyncModal(true);
  };

  const executeSync = async (wabaIdToSync) => {
    setIsSyncing(true);
    setShowSyncModal(false);

    const targetWaba = wabaList.find((w) => w.waba_id === wabaIdToSync);
    const opName = ("CLOUD").toUpperCase();
    const opDisplay = opName ? `${opName} API` : "WhatsApp Cloud";

    const targetLabel =
      wabaIdToSync === "all"
        ? "all provisioned WABAs"
        : `WABA ID ${wabaIdToSync}${targetWaba?.phone_number ? ` (${targetWaba.phone_number})` : ""}`;

    Swal.fire({
      title: "Syncing Templates...",
      html: `Connecting to <b>${opDisplay}</b> for <b>${targetLabel}</b>...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const payload = { waba_id: wabaIdToSync };
      if (filterAccountId && filterAccountId !== "all") {
        payload.accountId = filterAccountId;
      }
      const res = await templateService.syncTemplates(payload);
      if (res && res.success) {
        await Swal.fire({
          icon: "success",
          title: "Templates Synchronized!",
          text: res.message || `Successfully synchronized templates.`,
          timer: 2500,
          showConfirmButton: false,
        });
        await loadTemplates();
        await loadWabaList();
      } else {
        Swal.fire({
          icon: "error",
          title: "Sync Failed",
          text: res?.error || res?.message || "Failed to synchronize templates.",
          confirmButtonColor: "#059669",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Sync Error",
        text:
          err.response?.data?.error ||
          err.message ||
          "An unexpected error occurred while communicating with the operator API.",
        confirmButtonColor: "#059669",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  // Duplicate / Clone Template
  const handleDuplicateTemplate = (template) => {
    navigate('/templates/create', {
      state: { cloneTemplate: template }
    });
  };

  // Edit Template (Directly prefilled into create/edit screen)
  const handleEditTemplate = (template) => {
    navigate('/templates/create', {
      state: { editTemplate: template }
    });
  };

  // Delete Template with SweetAlert2 confirmation
  const handleDeleteTemplate = async (template) => {
    const name = template.str_template_name || template.name || "this template";
    const deleteId = template.int_template_id || template._id || template.id || template.str_template_name;

    const result = await Swal.fire({
      title: "Delete Template?",
      text: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Deleting Template...",
      text: "Please wait while the template is being removed.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const params = {};
      if (template.str_waba_id) params.waba_id = template.str_waba_id;
      if (filterAccountId && filterAccountId !== "all") params.accountId = filterAccountId;

      const res = await templateService.delete(deleteId, params);
      if (res && (res.success || res.status === 200 || !res.error)) {
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: `Template "${name}" has been deleted successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
        await loadTemplates();
      } else {
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: res?.error || res?.message || "Failed to delete the template.",
          confirmButtonColor: "#059669",
        });
      }
    } catch (err) {
      console.error("Delete error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.error || err.message || "Failed to delete template.",
        confirmButtonColor: "#059669",
      });
    }
  };

  const getFilteredTemplates = () => {
    let filtered = [...templates];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((t) => {
        const name = (t.str_template_name || t.name || "").toLowerCase();
        const waba = (t.str_waba_id || "").toLowerCase();
        return name.includes(q) || waba.includes(q);
      });
    }

    if (filterWabaId !== "all") {
      filtered = filtered.filter((t) => (t.str_waba_id || "") === filterWabaId);
    }

    if (filterCategory !== "all") {
      filtered = filtered.filter((t) => (t.str_category || t.category || "").toUpperCase() === filterCategory.toUpperCase());
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((t) => (t.str_status || t.status || "").toUpperCase() === filterStatus.toUpperCase());
    }

    // Newly created / updated templates first
    filtered.sort((a, b) => {
      const timeA = new Date(a.dttm_updated_on || a.dttm_created_on || a.updated_at || a.created_at || 0).getTime();
      const timeB = new Date(b.dttm_updated_on || b.dttm_created_on || b.updated_at || b.created_at || 0).getTime();
      if (timeB !== timeA) return timeB - timeA;
      const idA = Number(a.int_template_id) || 0;
      const idB = Number(b.int_template_id) || 0;
      return idB - idA;
    });

    return filtered;
  };

  const getStats = () => {
    const total = templates.length;
    const approved = templates.filter(t => (t.str_status || t.status) === "APPROVED").length;
    const pending = templates.filter(t => (t.str_status || t.status) === "PENDING").length;
    const rejected = templates.filter(t => (t.str_status || t.status) === "REJECTED").length;
    return { total, approved, pending, rejected };
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getCategoryBadge = (category) => {
    switch (category?.toLowerCase()) {
      case "utility":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
      case "marketing":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200";
      case "authentication":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const formatWhatsAppText = (text) => {
    if (!text) return "";
    let formatted = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    formatted = formatted
      .replace(/```([\s\S]*?)```/g, '<code class="font-mono bg-black/5 px-1 py-0.5 rounded text-xs">$1</code>')
      .replace(/\*([^\*]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      .replace(/~([^~]+)~/g, '<del>$1</del>')
      .replace(/(\{\{\d+\}\})/g, '<span class="bg-blue-100 text-blue-800 font-semibold px-1 rounded text-xs dark:bg-blue-900/60 dark:text-blue-300">$1</span>');
    return formatted;
  };

  const filteredTemplates = getFilteredTemplates();
  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage) || 1;
  const startIndex = (currentPage - 1) * templatesPerPage;
  const paginatedTemplates = filteredTemplates.slice(startIndex, startIndex + templatesPerPage);
  const stats = getStats();

  if (loading && templates.length === 0) {
    return (
      <div className="py-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              Message Templates
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Manage your WhatsApp approved message templates and personalization
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleOpenSync}
              disabled={isSyncing}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 mr-2 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Syncing..." : "Sync Templates"}
            </button>
            <button
              onClick={() => navigate('/templates/create')}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-sm shadow-green-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Template
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Total Templates</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Approved</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Pending</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Rejected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates or WABA ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Account Filter (Admin/SuperAdmin) */}
          {!isClient && accounts.length > 0 && (
            <div className="relative min-w-[200px]">
              <select
                value={filterAccountId}
                onChange={(e) => {
                  setFilterAccountId(e.target.value);
                  setFilterWabaId("all");
                  setCurrentPage(1);
                }}
                className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Accounts / Clients ({accounts.length})</option>
                {accountOptions.map((acc) => (
                  <option key={acc.value} value={acc.value}>
                    {acc.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* WABA ID Filter */}
          <div className="relative min-w-[220px]">
            <select
              value={filterWabaId}
              onChange={(e) => {
                setFilterWabaId(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All WABA IDs ({wabaList.length})</option>
              {wabaList.map((w) => (
                <option key={w.waba_id} value={w.waba_id}>
                  WABA: {w.waba_id} {w.phone_number ? `(${w.phone_number})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div className="relative min-w-[150px]">
            <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-8 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All Categories</option>
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utility</option>
              <option value="AUTHENTICATION">Authentication</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="relative min-w-[130px]">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All Status</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          {/* Per-page Selector */}
          <div className="relative">
            <select
              value={templatesPerPage}
              onChange={(e) => {
                setTemplatesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Templates Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {paginatedTemplates.length === 0 ? (
          <div className="py-12 px-4 text-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Templates Found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
              {templates.length === 0
                ? "You don't have any templates yet. Click 'Sync Templates' to fetch your approved WhatsApp templates or add a new one."
                : "No templates match your current filters. Try adjusting your search or WABA ID filter criteria."}
            </p>
            {templates.length === 0 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={handleOpenSync}
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 text-sm cursor-pointer shadow-sm shadow-blue-500/20"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Sync Templates
                </button>
                <button
                  onClick={() => navigate('/templates/create')}
                  className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 text-sm cursor-pointer shadow-sm shadow-green-500/20"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Template
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-16">
                      #
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Template Info
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      WABA ID / Account
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedTemplates.map((template, index) => {
                    const name = template.str_template_name || template.name || "Unknown";
                    const language = template.str_language || template.language || "en_US";
                    const category = template.str_category || template.category || "MARKETING";
                    const status = template.str_status || template.status || "APPROVED";
                    const wabaId = template.str_waba_id || "-";
                    const company = template.str_company_name || "";
                    const id = template._id || template.id;

                    return (
                      <tr
                        key={id || index}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                          #{(currentPage - 1) * templatesPerPage + index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white font-mono">
                              {name}
                            </div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">
                              Language: {language}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1 items-start">
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/70 px-2.5 py-1 rounded-lg border border-gray-200 dark:border-gray-600">
                              {wabaId}
                            </span>
                            {company && (
                              <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                                {company}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${getCategoryBadge(category)}`}>
                            {category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusBadge(status)}`}>
                            {status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-1">
                            {/* View / Preview Icon */}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedTemplate(template);
                                setIsModalOpen(true);
                              }}
                              className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200 cursor-pointer"
                              title="View / Preview Template"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>

                            {/* Edit Icon */}
                            <button
                              type="button"
                              onClick={() => handleEditTemplate(template)}
                              className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors duration-200 cursor-pointer"
                              title="Edit Template"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>

                            {/* Copy / Duplicate Icon */}
                            <button
                              type="button"
                              onClick={() => handleDuplicateTemplate(template)}
                              className="p-1.5 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors duration-200 cursor-pointer"
                              title="Clone / Duplicate Template"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            </button>

                            {/* Delete Icon */}
                            <button
                              type="button"
                              onClick={() => handleDeleteTemplate(template)}
                              className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200 cursor-pointer"
                              title="Delete Template"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Showing {(currentPage - 1) * templatesPerPage + 1} to{" "}
                  {Math.min(currentPage * templatesPerPage, filteredTemplates.length)} of{" "}
                  {filteredTemplates.length} templates
                </div>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, currentPage - 2);
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors ${currentPage === pageNum
                          ? "bg-green-600 text-white"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modal for Selecting WABA ID to Sync (When Multiple WABA IDs Exist) ── */}
      {showSyncModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-50/70 to-indigo-50/50 dark:from-gray-800 dark:to-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold shadow-md shadow-blue-500/20">
                  <ArrowPathIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Sync WhatsApp Templates
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Select WABA ID to synchronize approved templates from Meta
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSyncModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-2">
                  Target WhatsApp Business Account (WABA ID)
                </label>
                <select
                  value={selectedSyncWaba}
                  onChange={(e) => setSelectedSyncWaba(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                >
                  <option value="all">
                    ✨ Sync All WABA Accounts ({wabaList.length} accounts)
                  </option>
                  {wabaList.map((w) => (
                    <option key={w.waba_id} value={w.waba_id}>
                      WABA: {w.waba_id} {w.phone_number ? `· ${w.phone_number}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl border border-gray-200 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 space-y-2">
                <div className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-200">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                  {selectedSyncWaba === "all" ? (
                    <span>All provisioned WABA accounts will be queried in sequence</span>
                  ) : (
                    <span>Selected WABA ID: {selectedSyncWaba}</span>
                  )}
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  Approved templates will be fetched from Meta Cloud API and updated in your catalog. Existing templates with matching IDs will be refreshed automatically.
                </p>
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-900/60 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSyncModal(false)}
                className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => executeSync(selectedSyncWaba)}
                className="inline-flex items-center px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm shadow-blue-500/20 cursor-pointer"
              >
                <ArrowPathIcon className="w-4 h-4 mr-2" />
                Sync Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Details & WhatsApp Live Preview Modal (Read-Only) */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTemplate(null);
        }}
        title="Template Details & Preview"
        maxWidth="max-w-5xl"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column - Read-Only Template Information */}
          <div className="lg:col-span-7 space-y-4">
            {/* Header Card with Template Name & Badges */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/80 rounded-2xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Template Name
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-base font-bold text-gray-900 dark:text-white break-all">
                      {tplName}
                    </span>
                    {tplName && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(tplName, "name")}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-200/60 dark:hover:bg-gray-700 transition-colors cursor-pointer shrink-0"
                        title="Copy Template Name"
                      >
                        {copiedKey === "name" ? (
                          <CheckIcon className="w-4 h-4 text-green-500" />
                        ) : (
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase shrink-0 ${getStatusBadge(
                    tplStatus
                  )}`}
                >
                  {tplStatus}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700/60">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${getCategoryBadge(
                    tplCategory
                  )}`}
                >
                  Category: {tplCategory}
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-200/70 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  Language: {getLanguageLabel(tplLanguage)} ({tplLanguage})
                </span>
                {tplContent.variables.length > 0 && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                    {tplContent.variables.length} Dynamic Variable
                    {tplContent.variables.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            {/* Technical Metadata Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  WABA Account ID
                </span>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                    {tplWaba || "—"}
                  </span>
                  {tplWaba && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(tplWaba, "waba")}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded transition-colors cursor-pointer"
                      title="Copy WABA ID"
                    >
                      {copiedKey === "waba" ? (
                        <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Provider Template ID
                </span>
                <div className="flex items-center justify-between mt-1">
                  <span
                    className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-200 truncate"
                    title={tplProviderId}
                  >
                    {tplProviderId || "—"}
                  </span>
                  {tplProviderId && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(tplProviderId, "providerId")}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded transition-colors cursor-pointer"
                      title="Copy Provider Template ID"
                    >
                      {copiedKey === "providerId" ? (
                        <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Header Content Section */}
            {(tplContent.headerText || tplContent.headerType !== "NONE") && (
              <div className="p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Header
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    Format: {tplContent.headerType}
                  </span>
                </div>
                <div className="text-xs text-gray-800 dark:text-gray-200 font-medium">
                  {tplContent.headerText || `[Media Header: ${tplContent.headerType}]`}
                </div>
              </div>
            )}

            {/* Body Message Section */}
            <div className="p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Message Body
                </span>
                <div className="flex items-center gap-2">
                  {tplContent.variables.length > 0 && (
                    <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                      Params: {tplContent.variables.join(", ")}
                    </span>
                  )}
                  {tplContent.bodyText && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(tplContent.bodyText, "body")}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
                    >
                      {copiedKey === "body" ? (
                        <>
                          <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                          <span className="text-green-600 dark:text-green-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                          <span>Copy Body</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <div className="p-3.5 bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-200/80 dark:border-gray-700 text-xs text-gray-800 dark:text-gray-200 font-mono leading-relaxed whitespace-pre-wrap break-words">
                {tplContent.bodyText || "No body text available."}
              </div>
            </div>

            {/* Footer Text Section */}
            {tplContent.footerText && (
              <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-1">
                  Footer
                </span>
                <div className="text-xs text-gray-600 dark:text-gray-400 italic">
                  {tplContent.footerText}
                </div>
              </div>
            )}

            {/* Buttons Section */}
            {tplContent.buttons && tplContent.buttons.length > 0 && (
              <div className="p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 block mb-2">
                  Action Buttons ({tplContent.buttons.length})
                </span>
                <div className="space-y-2">
                  {tplContent.buttons.map((btn, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                          {btn.type || "BUTTON"}
                        </span>
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {btn.text || `Button ${idx + 1}`}
                        </span>
                      </div>
                      {(btn.url || btn.phone_number) && (
                        <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                          {btn.url || btn.phone_number}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}


          
          </div>

          {/* Right Column - WhatsApp Live Preview with its bottom Close button */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="w-full flex flex-col items-center justify-start bg-gradient-to-b from-gray-50/70 to-gray-100/70 dark:from-[#1C1C1E]/60 dark:to-[#121214]/60 rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-white/5">
              <div className="w-full flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  WhatsApp Live Preview
                </h4>
              </div>
              <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-100/80 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full">
                Simulated
              </span>
            </div>

            {/* WhatsApp Device Container */}
            <div
              style={{
                width: "250px",
                height: "461px",
                backgroundColor: "#111827",
                borderRadius: "29px",
                padding: "2px",
                boxShadow: "0 20px 40px -15px rgba(0,0,0,0.3)",
              }}
              className="flex flex-col relative"
            >
              {/* Screen Inner */}
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#efeae2",
                  borderRadius: "28px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                {/* Status Bar */}
                <div
                  style={{
                    height: "26px",
                    backgroundColor: "#075e54",
                    color: "white",
                    fontSize: "11px",
                    padding: "0 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span className="font-semibold">9:41</span>
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <span>📶</span>
                    <span>🔋</span>
                  </div>
                </div>

                {/* WhatsApp Chat Header */}
                <div
                  style={{
                    backgroundColor: "#075e54",
                    padding: "6px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                  }}
                >
                  <div style={{ color: "white", fontSize: "14px", marginRight: "2px" }}>←</div>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      backgroundColor: "#128C7E",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "13px",
                      fontWeight: "bold",
                      color: "#fff",
                      border: "1.5px solid rgba(255,255,255,0.4)",
                    }}
                  >
                    {(tplCompany || "B").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#fff",
                        fontWeight: "600",
                        lineHeight: "1.2",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {tplCompany || "Verified Business"}
                    </div>
                    <div style={{ fontSize: "10px", color: "#d1fae5" }}>online</div>
                  </div>
                </div>

                {/* Chat Scrollable Area */}
                <div
                  style={{
                    flex: 1,
                    padding: "10px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  className="scrollbar-thin"
                >
                  {/* Bubble Container */}
                  <div
                    style={{
                      maxWidth: "92%",
                      alignSelf: "flex-end",
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                    }}
                  >
                    {/* Main WhatsApp Bubble */}
                    <div
                      style={{
                        backgroundColor: "#dcf8c6",
                        borderRadius: "10px 0px 10px 10px",
                        padding: "8px 10px",
                        boxShadow: "0 1px 1.5px rgba(0,0,0,0.13)",
                      }}
                    >
                      {/* Template Name Tag */}
                      <div
                        style={{
                          fontSize: "9.5px",
                          fontFamily: "monospace",
                          color: "#64748b",
                          marginBottom: "4px",
                          fontWeight: "600",
                        }}
                      >
                        {tplName || "template_name"}
                      </div>

                      {/* Header (Media / Text) */}
                      {tplContent.headerType !== "NONE" && (
                        <div style={{ marginBottom: "6px" }}>
                          {tplContent.headerType === "TEXT" && tplContent.headerText ? (
                            <div style={{ fontWeight: "700", fontSize: "13px", color: "#1e293b", lineHeight: "1.3" }}>
                              {tplContent.headerText}
                            </div>
                          ) : tplContent.headerType === "IMAGE" ? (
                            <div
                              style={{
                                width: "100%",
                                height: "105px",
                                backgroundColor: "#cbd5e1",
                                borderRadius: "6px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#475569",
                                fontSize: "11px",
                                gap: "4px",
                              }}
                            >
                              <PhotoIcon className="w-6 h-6 text-slate-500" />
                              <span>Image Header</span>
                            </div>
                          ) : tplContent.headerType === "DOCUMENT" ? (
                            <div
                              style={{
                                padding: "8px 10px",
                                backgroundColor: "#e2e8f0",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#334155",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              <DocumentTextIcon className="w-5 h-5 text-red-500" />
                              <span>Document Attachment</span>
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Body */}
                      <div
                        style={{
                          fontSize: "12.5px",
                          lineHeight: "1.45",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          color: "#1e293b",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: tplContent.bodyText
                            ? formatWhatsAppText(tplContent.bodyText)
                            : "Your message content will appear here...",
                        }}
                      />

                      {/* Footer */}
                      {tplContent.footerText && (
                        <div
                          style={{
                            fontSize: "10.5px",
                            color: "#64748b",
                            marginTop: "6px",
                            lineHeight: "1.3",
                          }}
                        >
                          {tplContent.footerText}
                        </div>
                      )}

                      {/* Timestamp & Read Checkmarks */}
                      <div
                        style={{
                          fontSize: "9.5px",
                          color: "#64748b",
                          textAlign: "right",
                          marginTop: "3px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "3px",
                        }}
                      >
                        <span>10:42 AM</span>
                        <span style={{ color: "#34b7f1", fontWeight: "bold" }}>✓✓</span>
                      </div>
                    </div>

                    {/* WhatsApp Action Buttons (stacked below bubble) */}
                    {tplContent.buttons && tplContent.buttons.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        {tplContent.buttons.map((btn, bIdx) => {
                          const bType = (btn.type || "").toUpperCase();
                          const isOtp = bType === "OTP";
                          const isOneTap = isOtp && btn.otp_type === "ONE_TAP";
                          const bText = isOneTap
                            ? (btn.autofill_text || btn.text || "Autofill")
                            : (btn.text || (isOtp ? "Copy code" : `Button ${bIdx + 1}`));
                          return (
                            <div
                              key={bIdx}
                              style={{
                                backgroundColor: "#ffffff",
                                color: "#00a884",
                                padding: "7px 10px",
                                borderRadius: "8px",
                                fontSize: "11.5px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                              }}
                            >
                              {bType === "PHONE_NUMBER" ? (
                                <PhoneIcon className="w-3 h-3" />
                              ) : bType === "URL" ? (
                                <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                              ) : isOtp && !isOneTap ? (
                                <DocumentDuplicateIcon className="w-3 h-3" />
                              ) : (
                                <ChatBubbleLeftEllipsisIcon className="w-3 h-3" />
                              )}
                              <span>{bText}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Input Bar */}
                <div
                  style={{
                    padding: "6px 8px",
                    backgroundColor: "#f0f2f5",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      backgroundColor: "#fff",
                      borderRadius: "18px",
                      padding: "6px 12px",
                      fontSize: "11.5px",
                      color: "#94a3b8",
                    }}
                  >
                    Type a message...
                  </div>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      backgroundColor: "#00a884",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "12px",
                    }}
                  >
                    🎙️
                  </div>
                </div>
              </div>
            </div>
            </div>
            {/* Close button outside WhatsApp preview card, at its bottom */}
            <div className="mt-3 flex items-center justify-end">
  {/* Read-Only Notice */}
            <div className="pt-2 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 italic">
              <span>🔒 WhatsApp approved templates are read-only and cannot be modified directly.</span>
            </div>
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedTemplate(null);
                }}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
